import api from './api';
import { Area, CreateAreaForm, UpdateAreaForm, CambiarEstadoAreaForm, CambiarDesactivacionAreaForm, ApiResponse, AreaTarea, CreateAreaTareaForm, ReordenAreaTareaForm, UpdateAreaTareaConfigForm } from '../types';

export const areaService = {
  async getAreas(): Promise<ApiResponse<Area[]>> {
    try {
      const response = await api.get('/Areas');
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al cargar las áreas',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async getAreasActivas(): Promise<ApiResponse<Area[]>> {
    try {
      const response = await api.get('/Areas/activas');
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al cargar las áreas activas',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async getAreaById(id: number): Promise<ApiResponse<Area>> {
    try {
      const response = await api.get(`/Areas/${id}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al cargar el área',
        errors: error.response?.data?.errors || [],
        sinConexion: !error.response,
      };
    }
  },

  async createArea(area: CreateAreaForm): Promise<ApiResponse<Area>> {
    try {
      const response = await api.post('/Areas', area);
      return { success: true, data: response.data };
    } catch (error: any) {
      // Manejar específicamente el error de límite de plan
      if (error.response?.data?.message?.includes('límite de áreas')) {
        return { 
          success: false, 
          message: 'Has alcanzado el límite de áreas de tu plan. Considera subir de plan para crear más áreas.',
          errors: ['Límite de plan alcanzado']
        };
      }
      
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al crear el área',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async updateArea(id: number, area: UpdateAreaForm): Promise<ApiResponse<Area>> {
    try {
      const response = await api.put(`/Areas/${id}`, area);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al actualizar el área',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async deleteArea(id: number): Promise<ApiResponse<void>> {
    try {
      await api.delete(`/Areas/${id}`);
      return { success: true };
    } catch (error: any) {
      if (error?.response?.status === 409) {
        return {
          success: false,
          message: error.response?.data?.message || 'No se puede eliminar el área porque tiene registros asociados.',
          errors: ['Conflict']
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar el área',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async getEstadoAreas(): Promise<ApiResponse<Area[]>> {
    try {
      const response = await api.get('/Areas/estado');
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al cargar el estado de las áreas',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async cambiarEstado(id: number, estado: CambiarEstadoAreaForm): Promise<ApiResponse<Area>> {
    try {
      const response = await api.patch(`/Areas/${id}/estado`, estado);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al cambiar el estado del área',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async cambiarDesactivacion(id: number, desactivacion: CambiarDesactivacionAreaForm): Promise<ApiResponse<Area>> {
    try {
      const response = await api.patch(`/Areas/${id}/desactivacion`, desactivacion);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al cambiar la desactivación del área',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // ====== Gestión de AreaTareas ======
  async listarAreaTareas(areaId: number): Promise<ApiResponse<AreaTarea[]>> {
    try {
      const response = await api.get(`/Areas/${areaId}/area-tareas`);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al listar tareas del área',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async agregarAreaTarea(areaId: number, payload: CreateAreaTareaForm): Promise<ApiResponse<AreaTarea>> {
    try {
      const response = await api.post(`/Areas/${areaId}/area-tareas`, payload);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al agregar tarea personalizada',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async excluirAreaTarea(areaId: number, tareaId: number): Promise<ApiResponse<void>> {
    try {
      await api.post(`/Areas/${areaId}/area-tareas/${tareaId}/excluir`, {});
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al excluir tarea',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async reincorporarAreaTarea(areaId: number, tareaId: number): Promise<ApiResponse<void>> {
    try {
      await api.post(`/Areas/${areaId}/area-tareas/${tareaId}/reincorporar`, {});
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al reincorporar tarea',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async reordenarAreaTareas(areaId: number, ordenes: ReordenAreaTareaForm[]): Promise<ApiResponse<void>> {
    try {
      await api.put(`/Areas/${areaId}/area-tareas/orden`, ordenes);
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al reordenar tareas',
        errors: error.response?.data?.errors || []
      };
    }
  },

  async actualizarConfigAreaTarea(areaId: number, tareaId: number, payload: UpdateAreaTareaConfigForm): Promise<ApiResponse<void>> {
    try {
      await api.patch(`/Areas/${areaId}/area-tareas/${tareaId}`, payload);
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al actualizar configuración de la tarea',
        errors: error.response?.data?.errors || []
      };
    }
  },
  
  async eliminarAreaTareaPersonalizada(areaId: number, tareaId: number): Promise<ApiResponse<void>> {
    try {
      await api.delete(`/Areas/${areaId}/area-tareas/${tareaId}`);
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al eliminar tarea personalizada',
        errors: error.response?.data?.errors || []
      };
    }
  },
}; 